<script lang="ts">
  import {
    ShieldCheck,
    ShieldAlert,
    Pin,
    PinOff,
    SwatchBook,
    Download,
    Sparkles
  } from "lucide-svelte";
  import type { DefaultCardResult } from "./defaultCard";
  import type { WalletCard } from "../wallet/types";
  import CardThumb from "./CardThumb.svelte";
  import { daysSinceReview, formatDate } from "./lib/format";
  import { cn } from "./lib/utils";

  export let result: DefaultCardResult;
  export let selectedCards: WalletCard[];
  export let dataReviewedAt = "";
  export let pinPickerOpen: boolean;
  export let exporting: boolean;
  export let onTogglePin: () => void;
  export let onPin: (cardId: string) => void;
  export let onUnpin: () => void;
  export let onExport: () => void;

</script>

<section class="anim-fade-in-up relative overflow-hidden rounded-3xl border bg-card glow-amber">
  <!-- ambient backdrop -->
  <div
    aria-hidden="true"
    class="pointer-events-none absolute inset-0 opacity-90"
    style="
      background:
        radial-gradient(ellipse 60% 50% at 20% 0%, hsl(var(--primary) / 0.18), transparent 60%),
        radial-gradient(ellipse 60% 60% at 100% 110%, hsl(var(--primary) / 0.10), transparent 60%);
    "
  ></div>

  <div class="relative px-6 pb-7 pt-6">
    <header class="mb-5 flex items-center justify-between">
      <div class="flex items-center gap-1.5">
        <Sparkles class="size-3.5 text-primary" />
        <span class="font-label text-muted-foreground">你的默认卡 · 随手刷</span>
      </div>
      {#if result.pinned}
        <span class="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-[10px] font-medium tracking-wider text-primary">
          <Pin class="size-3" />
          已置顶
        </span>
      {:else if result.trustedBenefitCount > 0}
        <span class="inline-flex items-center gap-1 text-[10px] font-medium tracking-wider text-success">
          <ShieldCheck class="size-3" />
          基于 {result.trustedBenefitCount} 条已核实
        </span>
      {:else if result.card}
        <span class="inline-flex items-center gap-1 text-[10px] font-medium tracking-wider text-warn">
          <ShieldAlert class="size-3" />
          暂无已核实权益
        </span>
      {/if}
    </header>

    {#if result.card}
      <div class="flex items-start gap-5">
        <div class="relative">
          <div
            aria-hidden="true"
            class="absolute -inset-3 rounded-2xl bg-gradient-to-br from-primary/25 via-primary/0 to-primary/15 blur-2xl"
          ></div>
          <CardThumb card={result.card} size="md" tilt class="relative" />
        </div>
        <div class="min-w-0 flex-1 pt-1">
          <h1 class="font-display text-[2.4rem] leading-[1.05] text-foreground">
            {result.card.shortName || result.card.name}
          </h1>
          <p class="mt-1.5 truncate text-xs text-muted-foreground">
            {result.card.issuer} · {result.card.level}
          </p>
        </div>
      </div>

      <!-- the verdict -->
      <div
        class="mt-6 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/12 via-transparent to-primary/8 px-5 py-4"
      >
        <p class="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary/80">
          支付方式
        </p>
        <p class="mt-1 text-lg font-semibold leading-snug text-foreground">{result.payHint}</p>
        <p class="mt-2 text-xs text-muted-foreground">{result.reason}</p>
        {#if result.topContributions.length > 0 && !result.pinned}
          <ul class="mt-3 space-y-1.5">
            {#each result.topContributions as c, i}
              <li class="anim-fade-in-up flex items-center gap-2 text-[11px]" style="animation-delay: {i * 60}ms;">
                <span class="flex size-4 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[9px] font-bold text-primary">
                  {i + 1}
                </span>
                <span class="min-w-0 flex-1 truncate text-muted-foreground">{c.benefit.title}</span>
                {#if c.benefit.estimateValueCny > 0}
                  <span class="shrink-0 text-[10px] tabular-nums text-primary/80">
                    {new Intl.NumberFormat("zh-CN", { style: "currency", currency: "CNY", maximumFractionDigits: 0 }).format(c.benefit.estimateValueCny)}
                  </span>
                {/if}
              </li>
            {/each}
          </ul>
        {/if}
        {#if result.noteWhenWeak}
          <p class="mt-3 rounded-md bg-warn/10 px-2.5 py-1.5 text-[11px] text-warn">
            {result.noteWhenWeak}
          </p>
        {/if}
      </div>

      <!-- actions -->
      <div class="mt-4 flex flex-wrap items-center gap-2">
        {#if result.pinned}
          <button
            type="button"
            class="focus-amber inline-flex items-center gap-1.5 rounded-full border border-input bg-background px-3 py-1.5 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
            on:click={onUnpin}
          >
            <PinOff class="size-3" />
            按算法重选
          </button>
        {:else}
          <button
            type="button"
            class="focus-amber inline-flex items-center gap-1.5 rounded-full border border-input bg-background px-3 py-1.5 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
            on:click={onTogglePin}
          >
            <SwatchBook class="size-3" />
            {pinPickerOpen ? "收起" : "换张卡当默认"}
          </button>
        {/if}
        <button
          type="button"
          class="focus-amber inline-flex items-center gap-1.5 rounded-full border border-input bg-background px-3 py-1.5 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
          on:click={onExport}
          disabled={exporting}
        >
          <Download class="size-3" />
          {exporting ? "导出中…" : "存为壁纸"}
        </button>
      </div>

      {#if pinPickerOpen && !result.pinned}
        <div class="anim-fade-in-up mt-4 rounded-2xl border border-dashed border-primary/30 bg-background/50 p-4">
          <p class="mb-3 text-[11px] text-muted-foreground">选一张固定当默认；可随时撤回。</p>
          <div class="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {#each selectedCards as c}
              {@const isCurrent = result.card?.id === c.id}
              <button
                type="button"
                class={cn(
                  "focus-amber rounded-xl border bg-card px-3 py-2.5 text-left text-xs transition-all hover:-translate-y-0.5 hover:border-primary",
                  isCurrent && "border-primary ring-1 ring-primary/30"
                )}
                on:click={() => onPin(c.id)}
              >
                <div class="truncate font-medium text-foreground">{c.shortName || c.name}</div>
                <div class="mt-0.5 truncate text-[10px] text-muted-foreground">{c.issuer}</div>
              </button>
            {/each}
          </div>
        </div>
      {/if}

      <!-- exceptions -->
      {#if dataReviewedAt}
        {@const days = daysSinceReview(dataReviewedAt)}
        <div class="mt-3 flex items-center justify-between text-[10px] text-muted-foreground/80">
          <span class="inline-flex items-center gap-1">
            <span>规则数据于 {formatDate(dataReviewedAt)} 复核</span>
            {#if days !== null && days > 30}
              <span class="rounded-full bg-warn/10 px-1.5 py-0.5 text-warn">{days} 天前</span>
            {/if}
          </span>
        </div>
      {/if}

      {#if result.exceptions.length > 0}
        <div class="mt-6 border-t border-dashed pt-5">
          <p class="font-label text-muted-foreground">
            例外 · 背下来 {result.exceptions.length} 条
          </p>
          <ul class="mt-3 space-y-2">
            {#each result.exceptions as ex, i}
              <li
                class="anim-fade-in-up flex items-center gap-3 rounded-xl bg-secondary/60 px-3 py-2.5"
                style="animation-delay: {i * 70}ms;"
              >
                <span class="text-lg">{ex.emoji}</span>
                <span class="text-sm font-medium text-foreground">{ex.scenario}</span>
                <span class="text-muted-foreground/60">→</span>
                <span class="rounded-full bg-primary/15 px-2.5 py-0.5 text-[11px] font-semibold text-primary">
                  {ex.card.shortName || ex.card.name}
                </span>
                <span class="hidden truncate text-[11px] text-muted-foreground sm:inline">
                  {ex.benefit.title}
                </span>
              </li>
            {/each}
          </ul>
        </div>
      {:else if result.card && !result.pinned}
        <div class="mt-6 border-t border-dashed pt-5">
          <p class="text-sm text-muted-foreground">
            没有例外。任何消费都用默认卡，别想了。
          </p>
        </div>
      {/if}
    {:else}
      <p class="text-sm text-muted-foreground">还没选卡</p>
    {/if}
  </div>
</section>
